/**
 * BackButton - Visible back navigation button for the session flow.
 *
 * Displayed at the top of each step (except 'start') to provide
 * a clear, discoverable way to go back. Matches the DENiZEN
 * design system with soft borders and subtle shadows.
 *
 * Design: DENiZEN
 * - border, shadow-denizen-sm
 * - Left arrow icon + label
 * - WCAG 2.1 AA compliant (focus-visible ring, sufficient contrast)
 */

export interface BackButtonProps {
  /** Click handler - should call the goBack function from useBackButton */
  onClick: () => void
  /** Optional label override (defaults to "Back") */
  label?: string
  /** Whether the button is disabled */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
}

export function BackButton({
  onClick,
  label = 'Back',
  disabled = false,
  className = '',
}: BackButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Go back to the previous step`}
      className={`
        inline-flex items-center gap-2 px-4 py-2
        font-display font-bold text-sm tracking-wider
        border border-dark-green/15 bg-white text-dark-green rounded-full
        shadow-denizen-sm transition-all duration-150
        focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
        ${
          disabled
            ? 'opacity-40 cursor-not-allowed'
            : 'hover:-translate-y-1 hover:shadow-denizen active:translate-y-0 active:shadow-none cursor-pointer'
        }
        ${className}
      `}
    >
      <svg
        className="w-4 h-4"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z"
          clipRule="evenodd"
        />
      </svg>
      {label}
    </button>
  )
}

export default BackButton
